/**
 * §10 — every timed resource in the venue, with its live sessions.
 *
 * This used to be the `/resources` page's own state. It is a hook now because
 * the floor map is where the work happens: a PS5 station, a billiards table and
 * a kids area are places where people sit and consume, so they are acted on
 * from the map like any table, and the same data has to serve the map, the
 * on-map action menu and the panel that folds in what the page used to show.
 *
 * The one rule that survives the move intact: nothing here polls. The meters
 * are recomputed in the browser each second (`useMeterTick`) from the very
 * functions the API bills with, and the server is re-read only when a
 * `resource_session.*` event says something changed.
 */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { VENUE_EVENTS, type ResourceSessionEventPayload } from '@/lib/shared-types';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useRealtime } from '@/Hooks/useRealtime';
import { api } from '@/lib/api';
import { apiMessage } from './helpers';
import type { ResourceRow, SessionRow } from './sessions';

/**
 * Stable query arguments — a new object literal each render would be a new
 * cache key on every render.
 */
const RESOURCES_QUERY = { limit: 200, sortBy: 'name', sortOrder: 'asc' } as const;
const ACTIVE_QUERY = { limit: 200, status: 'ACTIVE' } as const;
const PAUSED_QUERY = { limit: 200, status: 'PAUSED' } as const;

export interface ResourceBoard {
  resources: ResourceRow[];
  /** Every live (ACTIVE or PAUSED) session, across every resource. */
  sessions: SessionRow[];
  /**
   * §11 — a kids area runs several sessions at once, so a resource maps to a
   * *list*. Single-occupancy resources simply have a list of one.
   */
  sessionsByResource: Map<string, SessionRow[]>;
  loading: boolean;
  /** Whether the *session* lists are still in flight, which is the half a
   *  meter depends on and not the same question as the resource list. */
  sessionsLoading: boolean;
  error: string | null;
  /** Whether this operator may read sessions at all — the meters depend on it. */
  canReadSessions: boolean;
  /** The resource an action is currently in flight for. */
  busyId: string | null;
  reload: () => void;
  reloadSessions: () => void;
  /** Pause or resume, the two moves with nothing to ask the operator first. */
  act: (session: SessionRow, path: 'pause' | 'resume', messageKey: string) => Promise<void>;
}

export function useResourceBoard(enabled: boolean): ResourceBoard {
  const { t } = useTranslation();
  const { can } = usePermission();
  const [busyId, setBusyId] = useState<string | null>(null);

  // `resource_sessions.view` is a separate key from `resources.view`: a role
  // that may see the stations but not their meters gets the map, not a 403.
  const canReadSessions = enabled && can('resource_sessions.view');

  const resourcesQuery = useApi<unknown>(enabled ? '/resources' : null, RESOURCES_QUERY);
  const activeQuery = useApi<unknown>(
    canReadSessions ? '/resource-sessions' : null,
    ACTIVE_QUERY,
  );
  const pausedQuery = useApi<unknown>(
    canReadSessions ? '/resource-sessions' : null,
    PAUSED_QUERY,
  );

  const resources = useMemo(
    () => rowsOf<ResourceRow>(resourcesQuery.data).rows,
    [resourcesQuery.data],
  );

  const sessions = useMemo(
    () => [
      ...rowsOf<SessionRow>(activeQuery.data).rows,
      ...rowsOf<SessionRow>(pausedQuery.data).rows,
    ],
    [activeQuery.data, pausedQuery.data],
  );

  const sessionsByResource = useMemo(() => {
    const map = new Map<string, SessionRow[]>();
    for (const session of sessions) {
      const rows = map.get(session.resourceId) ?? [];
      rows.push(session);
      map.set(session.resourceId, rows);
    }
    for (const rows of map.values()) {
      rows.sort((a, b) => (a.startedAt ?? '').localeCompare(b.startedAt ?? ''));
    }
    return map;
  }, [sessions]);

  const reloadSessions = useCallback(() => {
    activeQuery.reload();
    pausedQuery.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuery.reload, pausedQuery.reload]);

  const reload = useCallback(() => {
    reloadSessions();
    resourcesQuery.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadSessions, resourcesQuery.reload]);

  // §10.1 events — start/resume, pause, extend and end all reshape the list.
  useRealtime<ResourceSessionEventPayload>(VENUE_EVENTS.RESOURCE_SESSION_STARTED, reload);
  useRealtime<ResourceSessionEventPayload>(VENUE_EVENTS.RESOURCE_SESSION_PAUSED, reload);
  useRealtime<ResourceSessionEventPayload>(VENUE_EVENTS.RESOURCE_SESSION_EXTENDED, reload);
  useRealtime<ResourceSessionEventPayload>(VENUE_EVENTS.RESOURCE_SESSION_ENDED, reload);
  useRealtime<ResourceSessionEventPayload>(VENUE_EVENTS.RESOURCE_SESSION_EXPIRING, (payload) => {
    const resource = resources.find((candidate) => candidate.id === payload.resourceId);
    toast.warning(
      t('spaces.resources.expiring', {
        name: resource?.name ?? '',
        count: Math.max(0, Math.round((payload.remainingSeconds ?? 0) / 60)),
      }),
    );
    reloadSessions();
  });

  const act = useCallback(
    async (session: SessionRow, path: 'pause' | 'resume', messageKey: string) => {
      setBusyId(session.resourceId);
      try {
        await api.post(`/resource-sessions/${session.id}/${path}`, {});
        toast.success(t(messageKey));
        reload();
      } catch (error) {
        toast.error(apiMessage(error, t('spaces.resources.actionFailed')));
      } finally {
        setBusyId(null);
      }
    },
    [reload, t],
  );

  return {
    resources,
    sessions,
    sessionsByResource,
    loading: resourcesQuery.loading,
    sessionsLoading: activeQuery.loading || pausedQuery.loading,
    error: resourcesQuery.error,
    canReadSessions,
    busyId,
    reload,
    reloadSessions,
    act,
  };
}

/**
 * The status to paint a resource with, given what is running on it.
 *
 * The row's own `status` column is the resource's *availability*; a live meter
 * is the more specific truth and is what the card already shows, so the map
 * must agree with it rather than telling a second story in a second colour.
 */
export function liveResourceStatus(fallback: string, sessions: SessionRow[] | undefined): string {
  if (sessions?.some((session) => session.status === 'ACTIVE')) return 'IN_USE';
  if (sessions?.length) return 'PAUSED';
  return fallback;
}
